/**
 * gwp-auto-add.js — gift-with-purchase cart automation.
 *
 * Keeps the cart in the only state checkout accepts: exactly one gift when
 * the amount actually paid for goods (total_price — after every discount,
 * before shipping — minus the gift lines) reaches the minimum spend, and no
 * gift below it. Config comes from Theme Settings ›
 * Gift with purchase via window.theme.gwpData (snippets/gwp-data.liquid); the
 * threshold is authored in shop currency and converted with the shared tier
 * engine's toPresentment().
 *
 * The gift product is priced $0, so the theme is not the security boundary —
 * a cart validation function (Functions Creator: max 1 gift, gift needs $60)
 * blocks checkout for any cart this script didn't get to. This script exists
 * so shoppers never see that error.
 *
 * A shopper who deletes the gift while still eligible has opted out: that is
 * remembered for the session (sessionStorage) and the gift is not re-added.
 */
(function () {
  'use strict';

  var DISMISSED_KEY = 'theme:gwp:dismissed';
  var ADDED_KEY = 'theme:gwp:added';
  var busy = false;

  function config() {
    var data = window.theme && window.theme.gwpData;
    if (!data || data.enabled !== true || !data.variant_id || !data.min) return null;
    return data;
  }

  function minCents(data) {
    var BT = window.theme && window.theme.bundleTiers;
    return BT ? BT.toPresentment(data.min) : data.min;
  }

  function readFlag(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key, on) {
    try {
      if (on) {
        sessionStorage.setItem(key, '1');
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      /* private mode — degrade to add-only behaviour */
    }
  }

  function refreshCartUI() {
    if (document.getElementById('cart-drawer')) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } else if (window.location.pathname.indexOf('/cart') === 0) {
      window.location.reload();
    }
  }

  function addGift(data) {
    busy = true;
    fetch(window.theme.routes && window.theme.routes.cart_add_url ? window.theme.routes.cart_add_url + '.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: data.variant_id, quantity: 1 }),
    })
      .then(function (res) {
        if (res.ok) {
          writeFlag(ADDED_KEY, true);
          refreshCartUI();
        }
      })
      .catch(function () {
        /* sold out or race — try again on the next cart change */
      })
      .finally(function () {
        busy = false;
      });
  }

  // One request for every gift line, keyed by line key so duplicate lines
  // (same variant, different properties) are handled too.
  function setGiftLines(updates) {
    busy = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (res) {
        if (res.ok) refreshCartUI();
        return res.ok;
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        busy = false;
      });
  }

  function removeGifts(giftLines) {
    var updates = {};
    giftLines.forEach(function (line) {
      updates[line.key] = 0;
    });
    return setGiftLines(updates);
  }

  function limitToOneGift(giftLines) {
    var updates = {};
    giftLines.forEach(function (line, index) {
      updates[line.key] = index === 0 ? 1 : 0;
    });
    return setGiftLines(updates);
  }

  function evaluate() {
    var data = config();
    if (!data || busy) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart || busy) return;

        var giftLines = cart.items.filter(function (item) {
          return item.product_id === data.product_id;
        });
        var giftQty = 0;
        var eligibleTotal = cart.total_price;
        giftLines.forEach(function (line) {
          giftQty += line.quantity;
          eligibleTotal -= line.final_line_price;
        });

        if (eligibleTotal < minCents(data)) {
          // Below the threshold the opt-out resets — crossing it again is a
          // fresh offer. The gift goes whoever added it.
          writeFlag(DISMISSED_KEY, false);
          writeFlag(ADDED_KEY, false);
          if (giftLines.length) removeGifts(giftLines);
          return;
        }

        if (giftQty > 1) {
          limitToOneGift(giftLines);
          return;
        }

        if (giftQty === 1) return;

        if (readFlag(ADDED_KEY)) {
          // We added it this session and it's gone while still eligible:
          // the shopper removed it on purpose. Respect that.
          writeFlag(DISMISSED_KEY, true);
          writeFlag(ADDED_KEY, false);
          return;
        }
        if (!readFlag(DISMISSED_KEY)) addGift(data);
      })
      .catch(function () {});
  }

  var debounceTimer = null;
  function scheduleEvaluate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 150);
  }

  document.addEventListener('theme:cart:added', scheduleEvaluate);
  document.addEventListener('theme:cart:update', scheduleEvaluate);
  document.addEventListener('theme:cart:loaded', scheduleEvaluate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEvaluate);
  } else {
    scheduleEvaluate();
  }
})();
